// src/components/admin/SaleBanner.tsx

import React, { useState, useEffect } from 'react';
import { Clock, Percent, Tag } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
import { formatDateEnglish, formatDateShortEnglish, formatDateTimeEnglish } from '../../utils/date';

interface SaleBannerProps {
    discountPercent: number;
    saleExpiry: string;
    salePrice: number;
    onEndSale: () => void;
    variantId?: number | null;
    compact?: boolean;
    saleStart?: string | null;
    isScheduled?: boolean;
    disabled: boolean;
}

export const SaleBanner: React.FC<SaleBannerProps> = ({
    discountPercent,
    saleExpiry,
    salePrice,
    onEndSale,
    compact = false,
    saleStart,
    disabled,
}) => {
    const [timeLeft, setTimeLeft] = useState('');
    const isSaleScheduled = saleStart && (new Date(saleStart) > new Date()) && new Date(saleExpiry) > new Date(saleStart);
    const isSaleActive = new Date(saleStart ?? '') < new Date() && new Date(saleExpiry) > new Date();
    
    useEffect(() => {
        if (!saleExpiry) return;

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const expiry = new Date(saleExpiry).getTime();
            const diff = expiry - now;

            if (diff <= 0) {
                setTimeLeft('Expired');
                clearInterval(interval);
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            if (days > 0) {
                setTimeLeft(`${days}d ${hours}h ${minutes}m`);
            } else if (hours > 0) {
                setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
            } else {
                setTimeLeft(`${minutes}m ${seconds}s`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [saleExpiry]);

    if (compact) {
        return (
            <div className="flex items-center gap-3 text-sm">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    {discountPercent}% OFF
                </span>
                <span className="text-gray-500 line-through">{formatPrice(salePrice / (1 - discountPercent / 100))}</span>
                <span className="text-green-600 font-medium">{formatPrice(salePrice)}</span>
                <span className="text-gray-400 text-xs">· {timeLeft}</span>
            </div>
        );
    }
    
    if (isSaleScheduled) {
        return (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-yellow-700 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Sale Scheduled
                    </span>
                    <span className="text-sm text-yellow-600 font-medium">
                        {discountPercent}% off
                    </span>
                    <span className="text-sm text-gray-500">
                        Starts: {formatDateEnglish(saleStart)} at {new Date(saleStart).toLocaleTimeString()}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onEndSale}
                    disabled={disabled}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-red-600 hover:bg-red-50'
                        }`}
                >
                    Cancel Sale
                </button>
            </div>
        );
    }
    if (isSaleActive)
        return (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-green-700 flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        Sale Active!
                    </span>
                    <span className="text-sm text-green-600 font-medium">
                        {discountPercent}% off
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {timeLeft}
                    </span>
                    <span className="text-sm text-gray-400 line-through">
                        {formatPrice(salePrice / (1 - discountPercent / 100))}
                    </span>
                    <span className="text-sm font-bold text-green-600">
                        {formatPrice(salePrice)}
                    </span>
                    <span className="text-xs text-gray-500">
                        Ends: {formatDateTimeEnglish(saleExpiry)}
                    </span>
                </div>
                <button
                    onClick={onEndSale}
                    type="button"
                    disabled={disabled}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-red-600 hover:bg-red-50'
                        }`}
                >
                    End Sale
                </button>
            </div>
        );
};